import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { IUserViewModel } from 'viewModels';
import { CardUser } from '../Cards/CardUser';
import { TypeCardUser } from '../../types/enums';
import { useTheme } from 'styled-components';

interface UserSelectorProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedUsers: IUserViewModel[]) => void;
  selectedUsers: IUserViewModel[];
  availableUsers: IUserViewModel[];
  loading?: boolean;
  title?: string;
  hasMoreUsers?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export function UserSelector({
  open,
  onClose,
  onConfirm,
  selectedUsers,
  availableUsers,
  loading = false,
  title = 'Selecionar Usuários',
  hasMoreUsers = false,
  onLoadMore,
  loadingMore = false,
}: UserSelectorProps) {
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<IUserViewModel[]>([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState<IUserViewModel[]>([]);

  useEffect(() => {
    setTempSelectedUsers(selectedUsers);
  }, [selectedUsers, open]);

  useEffect(() => {
    const filtered = availableUsers.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [availableUsers, searchTerm]);

  const handleToggleUser = (user: IUserViewModel) => {
    const isSelected = tempSelectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      setTempSelectedUsers(prev => prev.filter(u => u.id !== user.id));
    } else {
      setTempSelectedUsers(prev => [...prev, user]);
    }
  };

  const handleRemoveUser = (userId: string) => {
    setTempSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const handleConfirm = () => {
    onConfirm(tempSelectedUsers);
    onClose();
  };

  const handleClose = () => {
    setTempSelectedUsers(selectedUsers);
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={false}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          maxHeight: '90vh',
          margin: { xs: '16px', sm: '32px' },
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon fontSize="small" color="primary" />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }} color={theme.colors.secondary} >
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        }}
      >
        {/* Barra de pesquisa */}
        <TextField
          fullWidth
          placeholder="Pesquisar usuários..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
        />

        {/* Usuários selecionados */}
        {tempSelectedUsers.length > 0 && (
          <Box
            mb={2}
            sx={{
              p: 2,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              borderRadius: '8px',
              border: '1px solid rgba(25, 118, 210, 0.2)',
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Usuários Selecionados ({tempSelectedUsers.length})
            </Typography>
            <Box
              display="flex"
              flexWrap="wrap"
              gap={1}
              sx={{
                maxHeight: '120px',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '2px',
                },
              }}
            >
              {tempSelectedUsers.map((user) => (
                <Chip
                  key={user.id}
                  label={user.name}
                  onDelete={() => handleRemoveUser(user.id)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: '28px',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                    '& .MuiChip-deleteIcon': {
                      fontSize: '16px',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Lista de usuários disponíveis */}
        <Typography variant="subtitle2" gutterBottom>
          Usuários Disponíveis
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(2, 1fr)',
              },
              gap: 1,
              width: '100%',
              maxWidth: '100%',
              '& > *': {
                minHeight: '120px',
                maxWidth: '100%',
                overflow: 'hidden',
              }
            }}
          >
            {filteredUsers.map((user) => {
              const isSelected = tempSelectedUsers.some(u => u.id === user.id);
              return (
                <Box
                  key={user.id}
                  sx={{
                    height: '100%',
                    minHeight: '120px',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    '& .MuiCard-root': {
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s ease-in-out',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }
                    }
                  }}
                >
                  <CardUser
                    currentUser={{
                      ...user,
                      selected: isSelected,
                    }}
                    typeUserCard={TypeCardUser.REQUEST_SERVICE}
                    onToggleSelect={() => handleToggleUser(user)}
                  />
                </Box>
              );
            })}
          </Box>
        )}

        {/* Botão de carregar mais usuários */}
        {hasMoreUsers && onLoadMore && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="outlined"
              onClick={onLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={20} /> : null}
              sx={{
                minWidth: '200px',
                py: 1,
                borderRadius: '8px',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                }
              }}
            >
              {loadingMore ? 'Carregando...' : 'Carregar Mais Usuários'}
            </Button>
          </Box>
        )}

        {filteredUsers.length === 0 && !loading && (
          <Box textAlign="center" p={2}>
            <Typography color="text.secondary">
              Nenhum usuário encontrado
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid rgba(0,0,0,0.12)',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': {
            minWidth: { xs: '100%', sm: 'auto' },
          }
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            order: { xs: 2, sm: 1 },
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: '120px' }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            order: { xs: 1, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: '140px' },
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Confirmar ({tempSelectedUsers.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
